import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Upload, Loader2, Image as ImageIcon, Coffee, Sparkles } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Sicak Icecekler', 'Soguk Icecekler', 'Meyveli Icecekler',
  'Kahveler', 'Caylar', 'Milkshake', 'Smoothie', 'Alkollü', 'Su & Soda', 'Diger'
];

const emptyBeverage = {
  name: '', description: '', price: '', category: '', image_url: '', is_available: true,
  is_featured: false,
  name_en: '', name_me: '', name_ru: '', name_sr: '',
  description_en: '', description_me: '', description_ru: '', description_sr: ''
};

const BeverageManagement = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyBeverage);
  const [uploading, setUploading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/admin/beverages`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch { toast.error('Icecekler yuklenemedi'); } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Resim yuklendi');
    } catch { toast.error('Resim yuklenemedi'); } finally { setUploading(false); }
  };

  const handleTranslate = async () => {
    if (!formData.name) { toast.error('Önce içecek adını girin'); return; }
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/admin/catering/translate`, {
        name: formData.name, category: formData.category || 'icecek'
      }, { headers: { Authorization: `Bearer ${token}` } });
      setFormData(prev => ({
        ...prev,
        name_en: res.data.name_en || prev.name_en,
        name_me: res.data.name_me || prev.name_me,
        name_ru: res.data.name_ru || prev.name_ru,
        name_sr: res.data.name_sr || prev.name_sr,
        description: prev.description || res.data.description || '',
        description_en: res.data.description_en || prev.description_en,
        description_me: res.data.description_me || prev.description_me,
        description_ru: res.data.description_ru || prev.description_ru,
        description_sr: res.data.description_sr || prev.description_sr
      }));
      toast.success('AI çeviri tamamlandı (5 dil)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Çeviri başarısız');
    } finally { setTranslating(false); }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.price || !formData.category) { toast.error('Gerekli alanlari doldurun'); return; }
    try {
      let data = { ...formData };
      const missingLangs = ['en', 'me', 'ru', 'sr'].some(l => !data[`name_${l}`]);
      if (missingLangs) {
        try {
          const tr = await axios.post(`${API}/admin/catering/translate`, {
            name: data.name, category: data.category || 'icecek'
          }, { headers: { Authorization: `Bearer ${token}` } });
          ['en', 'me', 'ru', 'sr'].forEach(l => {
            if (!data[`name_${l}`] && tr.data[`name_${l}`]) data[`name_${l}`] = tr.data[`name_${l}`];
            if (!data[`description_${l}`] && tr.data[`description_${l}`]) data[`description_${l}`] = tr.data[`description_${l}`];
          });
        } catch { /* non-blocking */ }
      }
      data = {
        ...data,
        price: parseFloat(data.price),
        is_featured: !!data.is_featured,
        name_en: data.name_en || null,
        name_me: data.name_me || null,
        name_ru: data.name_ru || null,
        name_sr: data.name_sr || null,
        description_en: data.description_en || null,
        description_me: data.description_me || null,
        description_ru: data.description_ru || null,
        description_sr: data.description_sr || null
      };
      if (editingItem) {
        await axios.put(`${API}/admin/beverages/${editingItem.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Icecek guncellendi');
      } else {
        await axios.post(`${API}/admin/beverages`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Icecek eklendi');
      }
      setShowDialog(false); setEditingItem(null);
      setFormData(emptyBeverage);
      fetchItems();
    } catch { toast.error('Islem basarisiz'); }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name, description: item.description, price: item.price.toString(),
      category: item.category, image_url: item.image_url, is_available: item.is_available,
      is_featured: item.is_featured || false,
      name_en: item.name_en || '', name_me: item.name_me || '',
      name_ru: item.name_ru || '', name_sr: item.name_sr || '',
      description_en: item.description_en || '', description_me: item.description_me || '',
      description_ru: item.description_ru || '', description_sr: item.description_sr || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Silmek istediginizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/beverages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Silindi'); fetchItems();
    } catch { toast.error('Silinemedi'); }
  };

  if (loading) return <div className="text-center py-8">Yukleniyor...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Coffee className="w-5 h-5 text-terracotta" />
          <h2 className="font-playfair text-xl font-semibold">Icecek Yonetimi</h2>
        </div>
        <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) { setEditingItem(null); setFormData(emptyBeverage); } }}>
          <DialogTrigger asChild><Button className="bg-basil text-white hover:bg-basil/90 rounded-full" data-testid="add-beverage-btn"><Plus className="w-4 h-4 mr-2" />Yeni Icecek</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingItem ? 'Icecek Duzenle' : 'Yeni Icecek'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label>Icecek Adi (Turkce) *</Label>
                  <Button type="button" size="sm" variant="outline" onClick={handleTranslate} disabled={translating || !formData.name}
                    className="text-xs rounded-full h-7 px-3 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="bev-translate-btn">
                    {translating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    {translating ? 'Çevriliyor...' : 'AI Çevir'}
                  </Button>
                </div>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1" data-testid="bev-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Icecek Adi (English)</Label><Input value={formData.name_en || ''} onChange={(e) => setFormData({ ...formData, name_en: e.target.value })} className="mt-1" placeholder="English name" /></div>
                <div><Label>Icecek Adi (Crnogorski)</Label><Input value={formData.name_me || ''} onChange={(e) => setFormData({ ...formData, name_me: e.target.value })} className="mt-1" placeholder="Montenegrin name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Icecek Adi (Русский)</Label><Input value={formData.name_ru || ''} onChange={(e) => setFormData({ ...formData, name_ru: e.target.value })} className="mt-1" placeholder="Russian name (Cyrillic)" data-testid="bev-name-ru" /></div>
                <div><Label>Icecek Adi (Српски)</Label><Input value={formData.name_sr || ''} onChange={(e) => setFormData({ ...formData, name_sr: e.target.value })} className="mt-1" placeholder="Serbian name (Cyrillic)" data-testid="bev-name-sr" /></div>
              </div>
              <div><Label>Aciklama (Turkce)</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-1" data-testid="bev-desc-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Aciklama (English)</Label><Textarea value={formData.description_en || ''} onChange={(e) => setFormData({ ...formData, description_en: e.target.value })} className="mt-1" rows={2} /></div>
                <div><Label>Aciklama (Crnogorski)</Label><Textarea value={formData.description_me || ''} onChange={(e) => setFormData({ ...formData, description_me: e.target.value })} className="mt-1" rows={2} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Aciklama (Русский)</Label><Textarea value={formData.description_ru || ''} onChange={(e) => setFormData({ ...formData, description_ru: e.target.value })} className="mt-1" rows={2} data-testid="bev-desc-ru" /></div>
                <div><Label>Aciklama (Српски)</Label><Textarea value={formData.description_sr || ''} onChange={(e) => setFormData({ ...formData, description_sr: e.target.value })} className="mt-1" rows={2} data-testid="bev-desc-sr" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Fiyat</Label><Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="mt-1" data-testid="bev-price-input" /></div>
                <div><Label>Kategori</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="mt-1" data-testid="bev-category-select"><SelectValue placeholder="Secin" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Urun Gorseli</Label>
                <div className="mt-2 space-y-3">
                  {formData.image_url && (
                    <div className="relative w-full h-32 rounded-lg overflow-hidden bg-stone-100">
                      <img src={formData.image_url.startsWith('/api') ? `${API.replace('/api', '')}${formData.image_url}` : formData.image_url} alt="Onizleme" className="w-full h-full object-cover" />
                      <button onClick={() => setFormData({ ...formData, image_url: '' })} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                    <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex-1">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {uploading ? 'Yukleniyor...' : 'Dosya Sec'}
                    </Button>
                  </div>
                  <div className="relative"><div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-charcoal/50">veya URL girin</span></div></div>
                  <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." />
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <Label className="flex items-center gap-2"><span className="text-amber-600">⭐</span> Şefin Önerisi</Label>
                <Switch checked={formData.is_featured} onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })} data-testid="bev-featured" />
              </div>
              <Button onClick={handleSubmit} className="w-full bg-terracotta text-white hover:bg-terracotta/90" data-testid="bev-submit-btn">{editingItem ? 'Guncelle' : 'Ekle'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-12">
          <Coffee className="w-16 h-16 text-charcoal/20 mx-auto mb-4" />
          <p className="text-charcoal/50">Henuz icecek eklenmemis</p>
          <p className="text-charcoal/40 text-sm mt-1">Yeni Icecek butonuna tiklayarak icecek ekleyin</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div key={item.id} className="border rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`bev-item-${item.id}`}>
              <div className="w-full h-32 bg-stone-100 relative">
                {item.image_url ? (
                  <img src={item.image_url.startsWith('/api') ? `${API.replace('/api', '')}${item.image_url}` : item.image_url} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : null}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Coffee className="w-12 h-12 text-stone-300" /></div>
                {!item.is_available && <div className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">Tukendi</div>}
                {item.is_featured && (
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-[10px] rounded-full font-bold shadow-md flex items-center gap-1">
                    <span>⭐</span> ŞEFİN ÖNERİSİ
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold">{item.name}</h3>
                  <span className="font-bold text-terracotta">€{item.price}</span>
                </div>
                <p className="text-xs text-charcoal/60 mb-2 line-clamp-2">{item.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs px-2 py-1 bg-sand rounded-full">{item.category}</span>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(item)} className="p-2 hover:bg-stone-100 rounded-lg"><Edit2 className="w-4 h-4 text-charcoal/60" /></button>
                    <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default BeverageManagement;
